import {
  UserResponseData,
  UpdateNewsletterResponse,
  ExperimentData
} from '@klab-berlin/api-sdk/lib/types/responses/User';
import {
  PaymentMethod,
  UpdatePayload as UserUpdatePayload,
  UpgradePayload
} from '@klab-berlin/api-sdk/lib/types/requests/User';
import { AsyncAction, AsyncActionStatus, SyncAction } from './common.actionTypes';
import {
  GetUpgradeOptionsResponse,
  GetUpgradePreviewResponse,
  PaymentMethodResponse,
  SubscriptionDetailsResponse,
  SubscriptionResponse,
  InvoicesResponse,
  SubscriptionPreviewUpgradeResponse,
  UpgradePlanResponse,
  RevocationIntakeResponse,
} from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { CheckoutPlan, RevocationIntakePayload } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import {
  SchoolInvoicesResponse,
  SchoolSeatsOverviewResponse
} from '@klab-berlin/api-sdk/lib/types/responses/Teacher';
import {
  CommonResponse,
  TeacherListResponse,
  SchoolAdminListResponse
} from '@klab-berlin/api-sdk/lib/types/responses/Teacher';
import { CancelOptions } from '@klab-berlin/api-sdk/lib/services/billing';

export enum FETCH_ME {
  START = 'FETCH_ME_START',
  SUCCESS = 'FETCH_ME_SUCCESS',
  ERROR = 'FETCH_ME_ERROR',
  EXPIRED = 'FETCH_ME_EXPIRED'
}

export type FetchMeAction = AsyncAction<
  typeof FETCH_ME.START,
  typeof FETCH_ME.SUCCESS,
  typeof FETCH_ME.ERROR | typeof FETCH_ME.EXPIRED,
  UserResponseData
>;

export enum UPDATE_ME {
  START = 'UPDATE_ME_START',
  SUCCESS = 'UPDATE_ME_SUCCESS',
  ERROR = 'UPDATE_ME_ERROR',
}

export type UpdateMeAction = AsyncAction<
  typeof UPDATE_ME.START,
  typeof UPDATE_ME.SUCCESS,
  typeof UPDATE_ME.ERROR,
  UserResponseData,
  UserUpdatePayload
  >;

export enum UPDATE_NEWSLETTER {
  START = 'UPDATE_NEWSLETTER_START',
  SUCCESS = 'UPDATE_NEWSLETTER_SUCCESS',
  ERROR = 'UPDATE_NEWSLETTER_ERROR',
}

export type UpdateNewsletterAction = AsyncAction<
  typeof UPDATE_NEWSLETTER.START,
  typeof UPDATE_NEWSLETTER.SUCCESS,
  typeof UPDATE_NEWSLETTER.ERROR,
  UpdateNewsletterResponse,
  { optIn: boolean }
  >;

export enum GET_SUBSCRIPTION {
  START = 'GET_SUBSCRIPTION_START',
  SUCCESS = 'GET_SUBSCRIPTION_SUCCESS',
  ERROR = 'GET_SUBSCRIPTION_ERROR',
}

export enum GET_SUBSCRIPTION_UPGRADE_PREVIEW {
  START = 'GET_SUBSCRIPTION_UPGRADE_PREVIEW_START',
  SUCCESS = 'GET_SUBSCRIPTION_UPGRADE_PREVIEW_SUCCESS',
  ERROR = 'GET_SUBSCRIPTION_UPGRADE_PREVIEW_ERROR',
}

export type GetSubscriptionAction = AsyncAction<
  typeof GET_SUBSCRIPTION.START,
  typeof GET_SUBSCRIPTION.SUCCESS,
  typeof GET_SUBSCRIPTION.ERROR,
  SubscriptionResponse | undefined,
  { userId: string, status?: AsyncActionStatus }
  >;

export type GetSubscriptionUpgradePreviewAction = AsyncAction<
  typeof GET_SUBSCRIPTION_UPGRADE_PREVIEW.START,
  typeof GET_SUBSCRIPTION_UPGRADE_PREVIEW.SUCCESS,
  typeof GET_SUBSCRIPTION_UPGRADE_PREVIEW.ERROR,
  SubscriptionPreviewUpgradeResponse | undefined,
  { userId: string, status?: AsyncActionStatus }
  >;

export enum CANCEL_SUBSCRIPTION {
    START = 'CANCEL_SUBSCRIPTION_START',
    SUCCESS = 'CANCEL_SUBSCRIPTION_SUCCESS',
    ERROR = 'CANCEL_SUBSCRIPTION_ERROR',
  }

export type CancelSubscriptionAction = AsyncAction<
    typeof CANCEL_SUBSCRIPTION.START,
    typeof CANCEL_SUBSCRIPTION.SUCCESS,
    typeof CANCEL_SUBSCRIPTION.ERROR,
    undefined,
    { userId: string, cancelOptions?: CancelOptions }
    >;

export enum GET_UPGRADE_OPTIONS {
  START = 'GET_UPGRADE_OPTIONS_START',
  SUCCESS = 'GET_UPGRADE_OPTIONS_SUCCESS',
  ERROR = 'GET_UPGRADE_OPTIONS_ERROR',
}

export type GetUpgradeOptionsAction = AsyncAction<
  typeof GET_UPGRADE_OPTIONS.START,
  typeof GET_UPGRADE_OPTIONS.SUCCESS,
  typeof GET_UPGRADE_OPTIONS.ERROR,
  GetUpgradeOptionsResponse,
  Record<string, never>
>;

export enum UPGRADE_PLAN {
  START = 'UPGRADE_PLAN_START',
  SUCCESS = 'UPGRADE_PLAN_SUCCESS',
  ERROR = 'UPGRADE_PLAN_ERROR',
}

export type UpgradePlanAction = AsyncAction<
  typeof UPGRADE_PLAN.START,
  typeof UPGRADE_PLAN.SUCCESS,
  typeof UPGRADE_PLAN.ERROR,
  UpgradePlanResponse,
  { targetPlanKey: CheckoutPlan }
>;

export enum GET_UPGRADE_PREVIEW {
  START = 'GET_UPGRADE_PREVIEW_START',
  SUCCESS = 'GET_UPGRADE_PREVIEW_SUCCESS',
  ERROR = 'GET_UPGRADE_PREVIEW_ERROR',
}

export type GetUpgradePreviewAction = AsyncAction<
  typeof GET_UPGRADE_PREVIEW.START,
  typeof GET_UPGRADE_PREVIEW.SUCCESS,
  typeof GET_UPGRADE_PREVIEW.ERROR,
  GetUpgradePreviewResponse,
  { targetPlanKey: CheckoutPlan }
>;

export enum GET_STRIPE_SUBSCRIPTION_DETAILS {
  START = 'GET_STRIPE_SUBSCRIPTION_DETAILS_START',
  SUCCESS = 'GET_STRIPE_SUBSCRIPTION_DETAILS_SUCCESS',
  ERROR = 'GET_STRIPE_SUBSCRIPTION_DETAILS_ERROR',
}

export type GetStripeSubscriptionDetailsAction = AsyncAction<
  typeof GET_STRIPE_SUBSCRIPTION_DETAILS.START,
  typeof GET_STRIPE_SUBSCRIPTION_DETAILS.SUCCESS,
  typeof GET_STRIPE_SUBSCRIPTION_DETAILS.ERROR,
  SubscriptionDetailsResponse,
  Record<string, never>
>;

// SM-117 — 14-day right-of-withdrawal (Widerruf) intake for Stripe subscriptions.
export enum SUBMIT_REVOCATION {
  START = 'SUBMIT_REVOCATION_START',
  SUCCESS = 'SUBMIT_REVOCATION_SUCCESS',
  ERROR = 'SUBMIT_REVOCATION_ERROR',
}

export type SubmitRevocationAction = AsyncAction<
  typeof SUBMIT_REVOCATION.START,
  typeof SUBMIT_REVOCATION.SUCCESS,
  typeof SUBMIT_REVOCATION.ERROR,
  RevocationIntakeResponse,
  RevocationIntakePayload
>;

export enum UPDATE_PAYMENT_METHOD {
  START = 'UPDATE_PAYMENT_METHOD_START',
  SUCCESS = 'UPDATE_PAYMENT_METHOD_SUCCESS',
  ERROR = 'UPDATE_PAYMENT_METHOD_ERROR',
}

export type UpdatePaymentMethodAction = AsyncAction<
  typeof UPDATE_PAYMENT_METHOD.START,
  typeof UPDATE_PAYMENT_METHOD.SUCCESS,
  typeof UPDATE_PAYMENT_METHOD.ERROR,
  PaymentMethodResponse,
  { paymentMethod: PaymentMethod, userId: string }
>;

export enum GET_PAYMENT_METHOD {
  START = 'GET_PAYMENT_METHOD_START',
  SUCCESS = 'GET_PAYMENT_METHOD_SUCCESS',
  ERROR = 'GET_PAYMENT_METHOD_ERROR',
}

export type GetPaymentMethodAction = AsyncAction<
  typeof GET_PAYMENT_METHOD.START,
  typeof GET_PAYMENT_METHOD.SUCCESS,
  typeof GET_PAYMENT_METHOD.ERROR,
  PaymentMethodResponse,
  { userId: string }
>;

export enum GET_INVOICES {
  START = 'GET_INVOICES_START',
  SUCCESS = 'GET_INVOICES_SUCCESS',
  ERROR = 'GET_INVOICES_ERROR',
}

export enum GET_SCHOOL_INVOICES {
  START = 'GET_SCHOOL_INVOICES_START',
  SUCCESS = 'GET_SCHOOL_INVOICES_SUCCESS',
  ERROR = 'GET_SCHOOL_INVOICES_ERROR',
}

export type GetSchoolInvoicesAction = AsyncAction<
  typeof GET_SCHOOL_INVOICES.START,
  typeof GET_SCHOOL_INVOICES.SUCCESS,
  typeof GET_SCHOOL_INVOICES.ERROR,
  SchoolInvoicesResponse
>;

export enum GET_SCHOOL_TEACHERS {
  START = 'GET_SCHOOL_TEACHERS_START',
  SUCCESS = 'GET_SCHOOL_TEACHERS_SUCCESS',
  ERROR = 'GET_SCHOOL_TEACHERS_ERROR',
}

export type GetInvoicesAction = AsyncAction<
  typeof GET_INVOICES.START,
  typeof GET_INVOICES.SUCCESS,
  typeof GET_INVOICES.ERROR,
  InvoicesResponse
>;

export type GetSchoolTeachersAction = AsyncAction<
  typeof GET_SCHOOL_TEACHERS.START,
  typeof GET_SCHOOL_TEACHERS.SUCCESS,
  typeof GET_SCHOOL_TEACHERS.ERROR,
  TeacherListResponse
>;

export enum GET_SCHOOL_ADMINS {
  START = 'GET_SCHOOL_ADMINS_START',
  SUCCESS = 'GET_SCHOOL_ADMINS_SUCCESS',
  ERROR = 'GET_SCHOOL_ADMINS_ERROR',
}

export type GetSchoolAdminsAction = AsyncAction<
  typeof GET_SCHOOL_ADMINS.START,
  typeof GET_SCHOOL_ADMINS.SUCCESS,
  typeof GET_SCHOOL_ADMINS.ERROR,
  SchoolAdminListResponse
>;

export enum UPGRADE {
  START = 'UPGRADE_START',
  SUCCESS = 'UPGRADE_SUCCESS',
  ERROR = 'UPGRADE_ERROR',
}

export enum UPGRADE_TO_KOMBI {
  START = 'UPGRADE_TO_START',
  SUCCESS = 'UPGRADE_TO_SUCCESS',
  ERROR = 'UPGRADE_TO_ERROR',
}

export type UpgradeAction = AsyncAction<
  typeof UPGRADE.START,
  typeof UPGRADE.SUCCESS,
  typeof UPGRADE.ERROR,
  UserResponseData,
  UpgradePayload
  >;

export type UpgradeToKombiAction = AsyncAction<
  typeof UPGRADE_TO_KOMBI.START,
  typeof UPGRADE_TO_KOMBI.SUCCESS,
  typeof UPGRADE_TO_KOMBI.ERROR,
  UserResponseData,
  UpgradePayload
  >;

export enum GET_EXPERIMENT {
  START = 'GET_EXPERIMENT_START',
  SUCCESS = 'GET_EXPERIMENT_SUCCESS',
  ERROR = 'GET_EXPERIMENT_ERROR',
}

export type GetExperimentAction = AsyncAction<
  typeof GET_EXPERIMENT.START,
  typeof GET_EXPERIMENT.SUCCESS,
  typeof GET_EXPERIMENT.ERROR,
  ExperimentData
>;

export enum TRACK_EXPERIMENT {
  START = 'TRACK_EXPERIMENT_START',
  SUCCESS = 'TRACK_EXPERIMENT_SUCCESS',
  ERROR = 'TRACK_EXPERIMENT_ERROR',
}

export type UpdateExperimentAction = AsyncAction<
  typeof TRACK_EXPERIMENT.START,
  typeof TRACK_EXPERIMENT.SUCCESS,
  typeof TRACK_EXPERIMENT.ERROR,
  ExperimentData
  >;

type IncrementDownloadsCounter = 'INCREMENT_DOWNLOADS_COUNTER';

export const INCREMENT_DOWNLOADS_COUNTER: IncrementDownloadsCounter = 'INCREMENT_DOWNLOADS_COUNTER';

export type IncrementDownloadsCounterAction = SyncAction<
  IncrementDownloadsCounter
>;

export enum GET_SCHOOL_SEATS {
  START = 'GET_SCHOOL_SEATS_START',
  SUCCESS = 'GET_SCHOOL_SEATS_SUCCESS',
  ERROR = 'GET_SCHOOL_SEATS_ERROR',
}

export type GetSchoolSeatsAction = AsyncAction<
  typeof GET_SCHOOL_SEATS.START,
  typeof GET_SCHOOL_SEATS.SUCCESS,
  typeof GET_SCHOOL_SEATS.ERROR,
  SchoolSeatsOverviewResponse
>;

export enum ADD_SCHOOL_TEACHER {
  START = 'ADD_SCHOOL_TEACHER_START',
  SUCCESS = 'ADD_SCHOOL_TEACHER_SUCCESS',
  ERROR = 'ADD_SCHOOL_TEACHER_ERROR',
}

export type AddSchoolTeacherAction = AsyncAction<
  typeof ADD_SCHOOL_TEACHER.START,
  typeof ADD_SCHOOL_TEACHER.SUCCESS,
  typeof ADD_SCHOOL_TEACHER.ERROR,
  CommonResponse
>;

export enum REMOVE_SCHOOL_TEACHER {
  START = 'REMOVE_SCHOOL_TEACHER_START',
  SUCCESS = 'REMOVE_SCHOOL_TEACHER_SUCCESS',
  ERROR = 'REMOVE_SCHOOL_TEACHER_ERROR',
}

export enum UPDATE_SCHOOL_TEACHER {
  START = 'UPDATE_SCHOOL_TEACHER_START',
  SUCCESS = 'UPDATE_SCHOOL_TEACHER_SUCCESS',
  ERROR = 'UPDATE_SCHOOL_TEACHER_ERROR',
}

export enum REMOVE_TEACHER_FROM_STORE {
  REMOVE = 'REMOVE_TEACHER_FROM_STORE_REMOVE',
}

export type RemoveSchoolTeacherAction = AsyncAction<
  typeof REMOVE_SCHOOL_TEACHER.START,
  typeof REMOVE_SCHOOL_TEACHER.SUCCESS,
  typeof REMOVE_SCHOOL_TEACHER.ERROR,
  CommonResponse
>;

export type UpdateSchoolTeacherAction = AsyncAction<
  typeof UPDATE_SCHOOL_TEACHER.START,
  typeof UPDATE_SCHOOL_TEACHER.SUCCESS,
  typeof UPDATE_SCHOOL_TEACHER.ERROR,
  CommonResponse
>;

export enum REMOVE_SCHOOL_ADMIN {
  START = 'REMOVE_SCHOOL_ADMIN_START',
  SUCCESS = 'REMOVE_SCHOOL_ADMIN_SUCCESS',
  ERROR = 'REMOVE_SCHOOL_ADMIN_ERROR',
}

export type RemoveSchoolAdminAction = AsyncAction<
  typeof REMOVE_SCHOOL_ADMIN.START,
  typeof REMOVE_SCHOOL_ADMIN.SUCCESS,
  typeof REMOVE_SCHOOL_ADMIN.ERROR,
  CommonResponse
>;

export type RemoveTeacherFromStoreAction = {
  type: typeof REMOVE_TEACHER_FROM_STORE.REMOVE;
  payload: { userId: string };
};

export enum ASSIGN_SCHOOL_ADMIN {
  START = 'ASSIGN_SCHOOL_ADMIN_START',
  SUCCESS = 'ASSIGN_SCHOOL_ADMIN_SUCCESS',
  ERROR = 'ASSIGN_SCHOOL_ADMIN_ERROR',
}

export type AssignSchoolAdminAction = AsyncAction<
  typeof ASSIGN_SCHOOL_ADMIN.START,
  typeof ASSIGN_SCHOOL_ADMIN.SUCCESS,
  typeof ASSIGN_SCHOOL_ADMIN.ERROR,
  CommonResponse
>;

export enum CREATE_SCHOOL_ADMIN {
  START = 'CREATE_SCHOOL_ADMIN_START',
  SUCCESS = 'CREATE_SCHOOL_ADMIN_SUCCESS',
  ERROR = 'CREATE_SCHOOL_ADMIN_ERROR',
}

export type CreateSchoolAdminAction = AsyncAction<
  typeof CREATE_SCHOOL_ADMIN.START,
  typeof CREATE_SCHOOL_ADMIN.SUCCESS,
  typeof CREATE_SCHOOL_ADMIN.ERROR,
  CommonResponse
>;

export enum SET_SELECTED_SCHOOL {
  ACTION = 'SET_SELECTED_SCHOOL'
}

export type SetSelectedSchoolAction = SyncAction<
  typeof SET_SELECTED_SCHOOL.ACTION,
  undefined,
  { schoolId: string }
>;
